<template lang="pug">
  .quiz.container(:class="{ completed: completed && !isOnQquiz }")
    .intro-text-wrap.row.xs-f-small.md-f-medium.max-at-tweak(v-if="introText")
      .fixed-width-tweak-upper
        .xs-12.text-center
          p.intro-text.xs-f-medium.max-at-tweak {{introText}}

    .row.xs-f-small.md-f-medium.max-at-tweak
      .fixed-width-tweak-upper
        .xs-12
          transition-group(name="fade" tag="div")
            Question(
              v-for="(question) in visibleQuestions" :key="question.key"
              :question="question" :quiz-key="quizKey"
              @question-answered="setAnswer"
              @question-text-input="setTextInput" @continue="continueQuestion"
              @key-enter="tryToSubmit" @skip="skipQuestion")

    .finish-button-wrap.xs-12(ref="finish-button-wrap" v-show="!hideCompletionButton")
      .scroll-to
      CompletionButton(v-if="quizCompletionButton" :completion-button="quizCompletionButton" @complete="finishQuiz")

    .xs-12
      transition-expand
        slot(v-if="completed" :current-question="currentQuestion" :finish-quiz="finishQuiz")

</template>

<script>
  import { defineAsyncComponent, reactive, watch, ref } from 'vue';
  import { mapActions, mapState } from 'vuex';
  import vueQuizSvc from '@services/vueQuizSvc';

  const CompletionButton = defineAsyncComponent(() => import(/* webpackChunkName: "quiz" */ './CompletionButton'));
  const Question = defineAsyncComponent(() => import(/* webpackChunkName: "quiz" */ './Question'));

  export default {
    name: "Quiz",
    emits: ['quiz-ready', 'initial-answers-set', 'scroll-to-finish-button', 'answer-set', 'quiz-completed'],

    components: {
      CompletionButton,
      Question
    },

    inject: ['getObjProperty', 'elementInView'],

    props: {
      introText: {
        type: String,
        required: false
      },
      // quiz level configs
      quizKey: {
        type: String,
        required: true
      },
      onCompleteMethod: {
        type: Function,
        required: false
      },
      questionNumberFormat: {
        type: String,
        default: '#'
      },
      quickEdit: Boolean,
      displayMode: String,
      noQuestionNumber: Boolean,
      noTracking: Boolean,
      noScrolling: Boolean,
      noAnimate: Boolean,
      noRedirect: Boolean,
      noQuestionText: Boolean,
      noButtons: Boolean,
      noContinueButtons: Boolean,
      noHistory: Boolean,
      noSurveyHistory: Boolean,
      noHintText: Boolean,
      noCheckAllText: Boolean,
      noBorder: Boolean,
      noAutoSave: Boolean,
      noNullifyAnswers: Boolean,
      prefillData: {
        type: Object,
        default: () => { },
        required: false
      },
      hideCompletionButton: {
        type: Boolean,
        default: false
      }
    },

    setup() {
      const quizCtl = ref({});
      const completed = ref(false);

      // using computed for completed value won't work because it's not reactive
      watch(() => quizCtl.value?.flowState, () => {
        completed.value = quizCtl.value?.flowState?.completed;
      }, { deep: true });

      return {
        quizCtl,
        completed
      };
    },

    computed: {
      // Cdata needed for email capture
      ...mapState('customer', ['cdata']),

      quiz() {
        return this.getObjProperty(this, 'quizCtl.quiz') || {};
      },

      questions() {
        return this.getObjProperty(this, 'quizCtl.quiz.questions') || [];
      },

      visibleQuestions() {
        return this.questions.filter(q => q._visible);
      },

      currentQuestion() {
        return this.getObjProperty(this, 'quizCtl.currentQuestion');
      },

      quizCompletionButton() {
        return this.getObjProperty(this, 'quizCtl.quiz.completionButton');
      },

      isOnQquiz() {
        //- need to keep the padding on the bottom of the quiz for onq_advisor
        return this.quizKey == 'onq_advisor';
      },

      inEmailCaptureExperiment() {
        return this.experiments["Advisor Email Capture 2022"] === "B";
      },
    },

    created() {
      vueQuizSvc.getQuizFlow({ name: this.quizKey }).then(res => {
        const quizFlow = reactive(res.data);

        vueQuizSvc.getCustomerAnswers().then(res => {
          this.$emit('quiz-ready');
          //- using dynamic import here to avoid putting QuizController in the main bundle
          import('@mrModules/QuizController').then(QuizCtl => {
            const quizCtl = new QuizCtl.default({
              // quiz level configs
              urlParams: this.$route.query,
              questionNumberFormat: this.questionNumberFormat,
              quickEdit: this.quickEdit,
              displayMode: this.displayMode,
              noQuestionNumber: this.noQuestionNumber,
              noTracking: this.noTracking,
              noScrolling: this.noScrolling,
              noAnimate: this.noAnimate,
              noRedirect: this.noRedirect,
              noQuestionText: this.noQuestionText,
              noButtons: this.noButtons,
              noContinueButtons: this.noContinueButtons,
              noHistory: this.noHistory,
              noSurveyHistory: this.noSurveyHistory,
              noHintText: this.noHintText,
              noCheckAllText: this.noCheckAllText,
              noBorder: this.noBorder,
              noAutoSave: this.noAutoSave,
              noNullifyAnswers: this.noNullifyAnswers,
              prefillData: this.prefillData,
              experiments: this.experiments,
            }, {
              onScrollToQuestion: this.scrollToQuestion,
              onScrollToFinishButton: this.scrollToFinishButton,
              onTrack: this.onTrack,
              onComplete: this.onComplete,
              onIncomplete: this.onIncomplete,
              notify: this.quizNotify,
              setCookie: this.$cookies.set,
              removeCookie: this.$cookies.remove
            }, quizFlow, reactive(res.data), vueQuizSvc, true /* use ref */);
            this.quizCtl = quizCtl;
            this.$emit('initial-answers-set', quizCtl.answers);
          });
        });
      });
    },

    methods: {
      ...mapActions(['notify']),
      ...mapActions('modal', ['showModal', 'hideModal']),

      //- ensure notify is compatible with QuizController
      quizNotify(notificationType, msg, duration) {
        let payload = {
          class: notificationType,
          message: msg,
          time: duration
        };
        this.notify(payload);
      },

      scrollToQuestion(question) {
        setTimeout(() => {
          this.scrollTo('.' + question.key + '-' + question._number + ' .scroll-to', 600);
          setTimeout(() => {
            let el = document.querySelector('.' + question.key + '-' + question._number + ' .question-number');

            if (el) {
              el.focus();
            }
          }, 600);
        }, 100);
      },

      scrollToFinishButton() {
        this.$emit('scroll-to-finish-button');
        if (this.elementInView(this.$refs['finish-button-wrap'])) {
          return;
        } else {
          setTimeout(() => {
            this.scrollTo('.finish-button-wrap .scroll-to', 600);
          }, 100);
        }
      },

      setAnswer(question, answer) {
        this.quizCtl.setAnswer(question, answer.value);
        this.$emit('answer-set', this.quizCtl.answers);
      },

      setTextInput(question, text) {
        this.quizCtl.setAdditionalText(question, text);
      },

      continueQuestion(question) {
        this.quizCtl.continueQuestion(question);
      },

      skipQuestion(question) {
        this.quizCtl.skipQuestion(question);
      },

      finishEmailCapture() {
        this.hideModal();
        this.quizCtl.finishQuiz();
      },

      finishQuiz() {
        if (this.inEmailCaptureExperiment && !this.cdata.email) {
          this.showModal({
            component: 'QuizEmailCaptureModal',
            props: {
              finishEmailCapture: this.finishEmailCapture,
            },
            theme: 'all-centered',
            disableClose: true
          });
        } else {
          this.quizCtl.finishQuiz();
        }
      },

      onTrack(payload) {
        if (typeof window.dataLayer != 'undefined' && this.quiz.key == 'advisor') {
          if (payload.eventName == 'Start Color Advisor') {
            window.dataLayer.push({
              'event': 'StartColorAdvisor',
              'quizName': this.quiz.key
            });
          }

          if (payload.eventName == 'Advisor Completed') {
            window.dataLayer.push({
              'event': 'AdvisorCompleted',
              'quizName': this.quiz.key
            });
          }
        }
      },

      onComplete(results) {
        if (this.onCompleteMethod) {
          this.onCompleteMethod(results);
        }

        this.$emit('quiz-completed');

        let answers = results?.answers;
        // special event for facebook tracking
        if (typeof window.fbq != 'undefined' && window.fbq && answers) {
          var fbqAnswers = {};
          Object.keys(answers).forEach((key) => {
            if (answers[key]) {
              fbqAnswers[key] = answers[key];
            }
          });
          window.fbq('trackCustom', this.quiz.key + 'Complete', fbqAnswers);
        }
      },

      onIncomplete() {
        this.quizNotify('error', 'question missing answer', 3000);
      },

      tryToSubmit() {
        // try to submit the completion button if the quiz is done and the last question was an input and enter key was pressed
        if (this.quizCompletionButton) {
          this.finishQuiz();
        }
      }
    },

  };
</script>

<style scoped lang="stylus">
  .quiz
    background-color color-gray-light
    padding-bottom 80vh
    transition(padding-bottom 350ms ease-in-out)
    -webkit-user-select none
    -khtml-user-select none
    -moz-user-select none
    -o-user-select none
    user-select none
    overflow hidden

    .intro-text-wrap
      background-color color-white

      .intro-text
        padding 1.5em 10%

    &.completed
      padding-bottom 0

    .question
      border-bottom 1px solid color-fog

      &:last-child
        border-bottom none

    .finish-button-wrap
      position relative

      .scroll-to
        position absolute
        left 0
        top -75vh
        pointer-events none

</style>
